/* Hauser MX — capa global de interacción (Fase 8).
 *
 * Tres cosas que están en TODAS las páginas y que, juntas, son lo que hace
 * que un sitio se sienta vivo antes de que el visitante lea una sola palabra:
 *
 *  1. Cursor magnético (solo puntero fino).
 *  2. Header que reacciona a la VELOCIDAD del scroll, no solo a la posición.
 *  3. Imán en botones y contadores que cuentan al entrar.
 *
 * Todo cuelga del bucle único de `hauser-motion-core.js`: ni un
 * `requestAnimationFrame` propio, ni un listener de `scroll` por efecto.
 */
(function () {
  'use strict';

  var H = window.Hauser;
  if (!H || H.tier === 'flat') return;

  var doc = document;

  /* ── 1. Cursor magnético ──────────────────────────────────────────── */

  /* No existe en táctil: ahí el dedo YA es el cursor, y un punto que persigue
     al dedo con retraso se ve como un fallo de rendimiento. Su papel lo hace
     el feedback de presión de `.hauser-magnetico` (ver hauser-inmersivo.css). */
  function cursor() {
    if (H.mqCoarse.matches) return;

    var el = doc.createElement('div');
    el.className = 'hauser-cursor';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<span class="hauser-cursor__punto"></span><span class="hauser-cursor__aro"></span><span class="hauser-cursor__texto"></span>';
    doc.body.appendChild(el);

    var punto = el.querySelector('.hauser-cursor__punto');
    var aro = el.querySelector('.hauser-cursor__aro');
    var texto = el.querySelector('.hauser-cursor__texto');

    var x = window.innerWidth / 2;
    var y = window.innerHeight / 2;
    var ax = x;
    var ay = y;

    H.porCuadro(function (_, dt) {
      var p = H.puntero;
      x = p.x * window.innerWidth;
      y = p.y * window.innerHeight;
      /* El punto va pegado al ratón y el aro va DETRÁS, con retraso. Esa
         diferencia de velocidad es todo el efecto: el aro se estira hacia
         donde vas y comunica dirección. */
      var k = 1 - Math.exp(-14 * (dt / 1000));
      ax += (x - ax) * k;
      ay += (y - ay) * k;

      punto.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
      aro.style.transform = 'translate3d(' + ax + 'px,' + ay + 'px,0)';
      texto.style.transform = 'translate3d(' + ax + 'px,' + ay + 'px,0)';
    });

    /* El cursor no aparece hasta que el ratón se mueve: si no, en un equipo
       con pantalla táctil Y ratón sale un punto flotando en el centro que
       nadie ha pedido. */
    window.addEventListener(
      'pointermove',
      function () { el.classList.add('is-visible'); },
      { once: true, passive: true }
    );

    doc.addEventListener('mouseleave', function () { el.classList.remove('is-visible'); });
    doc.addEventListener('mouseenter', function () { el.classList.add('is-visible'); });

    /* Delegación: el cursor cambia de forma según lo que hay debajo. Con
       delegación funciona también sobre lo que llegue por AJAX (el catálogo
       repinta al filtrar). */
    doc.addEventListener('pointerover', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      var etiqueta = t.closest('[data-cursor]');
      if (etiqueta) {
        el.classList.add('is-etiqueta');
        texto.textContent = etiqueta.getAttribute('data-cursor');
        return;
      }

      var media = t.closest('.hauser-card-propiedad__media, .hauser-mapa__marco, .hauser-hero__media');
      if (media) {
        el.classList.add('is-media');
        return;
      }

      if (t.closest('a, button, input, select, textarea, label, [role="button"]')) {
        el.classList.add('is-activo');
      }
    });

    doc.addEventListener('pointerout', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-cursor]')) { el.classList.remove('is-etiqueta'); texto.textContent = ''; }
      if (t.closest('.hauser-card-propiedad__media, .hauser-mapa__marco, .hauser-hero__media')) el.classList.remove('is-media');
      if (t.closest('a, button, input, select, textarea, label, [role="button"]')) el.classList.remove('is-activo');
    });
  }

  /* ── 2. Imán en botones ───────────────────────────────────────────── */

  /* El elemento se desplaza HACIA el puntero cuando está cerca. Es sutil a
     propósito (máximo ~7px): pasado de ahí el botón "huye" y se vuelve más
     difícil de pulsar, que es justo lo contrario de lo que se busca. */
  function imanes() {
    if (H.mqCoarse.matches) return;
    var gsap = window.gsap;
    if (!gsap) return;

    doc.addEventListener('pointermove', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('.hauser-magnetico') : null;
      if (!t) return;
      var r = t.getBoundingClientRect();
      var dx = e.clientX - (r.left + r.width / 2);
      var dy = e.clientY - (r.top + r.height / 2);
      gsap.to(t, { x: dx * 0.18, y: dy * 0.24, duration: 0.5, ease: 'power3.out', overwrite: 'auto' });
    });

    doc.addEventListener('pointerout', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('.hauser-magnetico') : null;
      if (!t) return;
      gsap.to(t, { x: 0, y: 0, duration: 0.8, ease: 'elastic.out(1, 0.4)', overwrite: 'auto' });
    });
  }

  /* ── 3. Header que reacciona a la velocidad ───────────────────────── */

  function header() {
    var grupo = doc.querySelector('#header-group');
    if (!grupo) return;

    var llave = grupo.querySelector('img, svg');
    var ultimo = 0;

    H.porCuadro(function () {
      var y = H.scroll.y;
      var v = H.scroll.velocidad;

      /* Encogido: a partir de media pantalla. Se compara contra el estado
         anterior para no tocar el DOM en cada cuadro. */
      var encogido = y > window.innerHeight * 0.5;
      if (encogido !== (ultimo === 1)) {
        grupo.classList.toggle('hauser-header--encogido', encogido);
        ultimo = encogido ? 1 : 0;
      }

      /* La llave del logo gira según la VELOCIDAD, no la posición. Es la
         diferencia entre una página que se mueve y una que responde: si
         paras, la llave vuelve sola a su sitio aunque sigas abajo. */
      if (llave && window.gsap) {
        window.gsap.set(llave, { rotate: v * 9 });
      }
    });
  }

  /* ── 4. Contadores ────────────────────────────────────────────────── */

  /* Cuentan al entrar en pantalla. Solo tocan la PARTE NUMÉRICA del texto:
     "+15 años" cuenta el 15 y conserva el "+" y el " años". Los valores que
     no son cifra ("A tu ritmo", "Uno a uno") se quedan quietos, que es lo
     correcto — no todos los pilares de confianza son números. */
  function contadores() {
    var nodos = doc.querySelectorAll('[data-hauser-contador]');
    if (!nodos.length || !window.gsap) return;

    Array.prototype.forEach.call(nodos, function (nodo) {
      var original = nodo.textContent.trim();
      var m = original.match(/^(\D*)(\d[\d,.]*)(.*)$/);
      if (!m) return;

      var antes = m[1];
      var destino = parseFloat(m[2].replace(/,/g, ''));
      var despues = m[3];
      if (!isFinite(destino)) return;

      var estado = { n: 0 };

      /* Poner el nodo en 0 SOLO si está por debajo del pliegue.
         El contador estaba escribiendo el 0 nada más arrancar, sin mirar
         dónde estaba el número. Para "+15 años", enterrado a media página,
         da igual. Pero desde el rediseño de la portada hay contadores en el
         pulso, que declara el inventario ("8 propiedades disponibles"): si
         ese nodo llegara a quedar en pantalla al cargar —una pantalla baja,
         un móvil en horizontal, un hero más corto— o si el observador no
         llegara a dispararse nunca, lo primero que leería el visitante sería
         "0 propiedades disponibles". Para una inmobiliaria ese cero no se
         lee como "cargando": se lee como "no tienen nada".
         Si ya está a la vista, el número real se queda y la animación
         simplemente no tiene nada que ocultar. */
      var caja = nodo.getBoundingClientRect();
      var fueraDePantalla = caja.top > (window.innerHeight || 0);
      if (fueraDePantalla) {
        nodo.textContent = antes + '0' + despues;
      }

      /* El margen del observador es CERO a propósito (antes era -12%).
         Con margen negativo, un contador que se queda justo en el filo
         inferior de la primera pantalla ya está a la vista pero todavía no
         ha "entrado" para el observador: se queda enseñando el 0 que
         acabamos de escribir hasta que alguien scrollea. Eso es exactamente
         lo que este bloque estaba tratando de evitar.

         Pasa de verdad: la tarjeta del hero de Contacto mide distinto
         cuando terminan de cargar las fuentes, sube unos píxeles y aparece
         en pantalla enseñando "+0" y "0 municipios". Con margen cero, en el
         momento en que asoma un píxel, cuenta. */
      H.enVista(nodo, function () {
        if (!fueraDePantalla) return;
        window.gsap.to(estado, {
          n: destino,
          duration: 1.6,
          ease: 'power2.out',
          onUpdate: function () {
            nodo.textContent = antes + Math.round(estado.n).toLocaleString('es-MX') + despues;
          },
          onComplete: function () {
            /* Se restaura el texto EXACTO del editor al terminar, para que el
               formato del cliente ("+15") mande sobre el de la animación. */
            nodo.textContent = original;
          }
        });
      }, { rootMargin: '0px' });
    });
  }

  /* ── 5. Titulares que entran por palabras ─────────────────────────── */

  /* Cualquier `[data-hauser-partir]` que NO esté dentro del hero de la portada
     (ese lo gobierna hauser-hero-gl.js, porque su entrada va sincronizada con
     el recorrido de scroll). Así las páginas interiores —Nosotros, Contacto,
     Vende tu propiedad, Preguntas frecuentes, 404 y el recorrido— heredan el
     mismo lenguaje de movimiento sin que haya que tocar ninguna de ellas: a
     todas les basta con marcar su titular. */
  function titulares() {
    var nodos = doc.querySelectorAll('[data-hauser-partir]');
    if (!nodos.length || !window.gsap) return;

    Array.prototype.forEach.call(nodos, function (el) {
      if (el.closest('[data-hauser-hero]')) return;

      var piezas = H.partirTexto(el);
      el.setAttribute('data-hauser-listo', '');
      if (!piezas.length) return;

      /* Estado inicial puesto por JS y no por CSS: si este script no llega a
         correr, el titular nunca se esconde. El fallo más caro posible en un
         encabezado es que se quede invisible. */
      window.gsap.set(piezas, { yPercent: 105 });

      H.enVista(el, function () {
        window.gsap.to(piezas, {
          yPercent: 0,
          duration: 0.9,
          ease: 'expo.out',
          stagger: 0.05
        });
      }, { rootMargin: '0px 0px -5% 0px' });
    });
  }

  H.listo(function () {
    cursor();
    imanes();
    header();
    contadores();
    titulares();
  });
})();
